import React, {useCallback, useEffect, useMemo, useState} from "react";
import {createUUId, dateComparator} from "../../../../core/services/utils/utils";
import PrintCustomerOutstandingStatementTemplate from "./template";
import PrintCustomerOutstandingStatementPage from "./page";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import Sizes from "../../../../core/models/static/sizes";
import ElementIds from "../../../../core/models/element-ids";
import {useHistory, useParams} from "react-router-dom";
import ToastService from "../../../../core/services/toast";
import {Button, DialogContent, DialogContentText, DialogTitle} from "@mui/material";
import Dialog from "../../../components/base/dialog";
import useQuery from "../../../hooks/use-query";
import {usePartnerInformation} from "../../../hooks/use-partner-information";
import usePrintPreviewScale from "../../../hooks/use-print-preview-scale";


export const PrintCustomerStatementViewTableTypes = {
    items: 'items',
}

const MaxInvoiceCountForStatement = 100;

/**
 * Fetches the elements that are needed for the computation of the print pages.
 * @return {{productsTable: {footer: HTMLElement, footerContinue: HTMLElement, header: HTMLElement, body: HTMLElement}, page: HTMLElement, pageHeader: HTMLElement, notesTable: {footerContinue: HTMLElement, header: HTMLElement, body: HTMLElement}}}
 */
export const getElements = () => ({
    page: document.getElementById(ElementIds.printCustomerOutstandingStatement.page),
    table: document.getElementById(ElementIds.printCustomerOutstandingStatement.table),
    pageHeader: document.getElementById(ElementIds.printCustomerOutstandingStatement.pageHeader),
    productsTable: {
        header: document.getElementById(ElementIds.printCustomerOutstandingStatement.productsTable.header),
        body: document.getElementById(ElementIds.printCustomerOutstandingStatement.productsTable.body),
        bodyFiller: document.getElementById(ElementIds.printCustomerOutstandingStatement.productsTable.bodyFiller),
        footerContinue: document.getElementById(ElementIds.printCustomerOutstandingStatement.productsTable.footerContinue),
        footer: document.getElementById(ElementIds.printCustomerOutstandingStatement.productsTable.footer),
    },
})

/**
 * Fetches the static heights of the template elements. These aid in the process of determining the print pages.
 * @param {any} elements
 */
export const getHeights = (elements) => ({
    productsTableStaticHeight: elements.productsTable.header.scrollHeight + elements.productsTable.footer.scrollHeight,
    productsTableStaticHeightWithContinue: elements.productsTable.header.scrollHeight + elements.productsTable.footerContinue.scrollHeight,
    productsTableFooterStaticHeight: elements.productsTable.footer.scrollHeight,
    productsTableFooterStaticHeightWithContinue: elements.productsTable.footerContinue.scrollHeight,
})

/**
 * @typedef {{
 *     pageNumber: int,
 *     totalPages: int,
 *     entities: {name: string, range: [number, number], useContinueFooter: boolean, spacerHeight: number}[],
 *     key: string,
 * }} PrintCustomerOutstandingStatementPageData
 */

const PrintCustomerOutstandingStatementView = () => {
    const params = useParams();
    const [query] = useQuery();
    const history = useHistory();
    const [printInfoReady, setPrintInfoReadyReady] = useState(false);
    const [statement, setStatement] = useState(null);
    const [showWarningDialog, setShowWarningDialog] = useState(false);
    const [pages, setPages] = useState([]);
    const isMounted = useIsMounted();

    const partnerInformation = usePartnerInformation();
    const {rootRef: printPreviewRootRef, style: printPreviewScaleStyle} = usePrintPreviewScale({
        pageElementId: ElementIds.printCustomerOutstandingStatement.page,
        ready: !!statement && !!partnerInformation,
    });
    const partner = useMemo(() => !partnerInformation ? partnerInformation : ({
        ...partnerInformation,
        logo: (partnerInformation?.configuration?.PartnerLogoUrlBaseAddress ?? '') + (partnerInformation?.logo ?? ''),
    }), [partnerInformation])
    const partnerInfoReady = !!partner;

    const customerId = useMemo(() => Number(params.customerId), [params.customerId]);
    const ready = useMemo(() => printInfoReady && partnerInfoReady, [printInfoReady, partnerInfoReady]);

    /**
     * Fetches the outstanding orders for the customer.
     * @type {(function(): Promise<*[]>)}
     */
    const getOutstandingTransactions = useCallback(async () => {
        const pagination = {currentPage: 1, pageSize: 100, length: 0};
        let result = [];
        do {
            const forApi = {
                pagination: pagination,
                sorts: [],
                customerId: customerId,
                showOutstandingOnly: true,
                startDateTime: query?.startDate,
                rndDateTime: query?.endDate,
            }
            const response = await Api.listArTransactions(forApi);
            if (!isMounted())
                return;
            if (response?.resultFlag) {
                result = [...result, ...(response.data?.items ?? [])];
                pagination.length = response.data?.pagination?.length ?? 0;
            }
        } while (pagination.currentPage * pagination.pageSize <= pagination.length);
        result.sort((a, b) => dateComparator(a.date, b.date));
        return result;
    }, [customerId, isMounted, query?.startDate, query?.endDate]);

    /**
     * Fetches the full information of the statement for the customer.
     */
    const getOrderInformation = useCallback(async () => {
        setPrintInfoReadyReady(false);
        const customerResponse = await Api.getCustomerFullInformation(customerId);
        if (!isMounted()) return;
        if (!customerResponse?.resultFlag) {
            ToastService.error({
                title: 'Error',
                message: `No customer information found for the selected customer`,
            });
            return history.goBack();
        }
        const customer = customerResponse.data;
        const arResponse = await Api.getARAgedList({
            keyword: customerResponse?.data?.code,
            pagination: {currentPage: 1, pageSize: 10},
        });
        if (!isMounted())
            return;
        if (!arResponse?.resultFlag) {
            ToastService.error({
                title: 'Error',
                message: `No AR information found for the selected customer with code ${customer?.code ?? "--"}`,
            });
            return history.goBack();
        }
        const foundAr = arResponse?.data?.items?.find(ar => ar.customerId === customer?.id);
        if (!foundAr) {
            ToastService.error({
                title: 'Error',
                message: `No AR information found for the selected customer with code ${customer?.code ?? "--"}`,
            });
            return history.goBack();
        }
        const transactionItems = await getOutstandingTransactions();
        if (!isMounted())
            return;
        setStatement({
            customer: customer,
            ar: foundAr,
            items: transactionItems,
        });
    }, [customerId, isMounted, getOutstandingTransactions, history])

    /**
     * Loads the selected order or credit as soon as the component loads
     */
    useEffect(() => {
        void getOrderInformation();
    }, [getOrderInformation])

    /**
     * Creates the pages that are to be rendered for printing.
     * @type {(function(elements: any, heights: any): PrintOrderPage[])}
     */
    const createPages = useCallback((elements, heights) => {
        const MAX_PAGE_HEIGHT = elements.page.clientHeight - (Sizes.defaultPagePadding * 2) - elements.pageHeader.scrollHeight;
        /**@type {HTMLCollection}*/
        const products = elements.productsTable.body.children;

        // Indices to keep track of which product and note are being processed
        let productsIndex = 0;

        // Array to store the pages
        let pages = [];
        let productsNormalFooterAdded = false;

        while (productsIndex < products.length) {
            let currentPageProducts = [];
            let currentPageNotes = [];
            let remainingHeight = MAX_PAGE_HEIGHT;
            let useProductsContinueFooter = false;

            // Add products
            while (productsIndex < products.length) {
                let nextProductHeight = products[productsIndex].scrollHeight;

                // Check if we can add more products without the "continue" footer
                if ((remainingHeight - nextProductHeight) < heights.productsTableStaticHeight) {
                    useProductsContinueFooter = true;
                    break;
                }

                currentPageProducts.push(productsIndex);
                remainingHeight -= nextProductHeight;
                productsIndex++;
            }

            // Try to add more products with the "continue" footer
            if (useProductsContinueFooter) {
                while (productsIndex < products.length) {
                    let nextProductHeight = products[productsIndex].scrollHeight;

                    if ((remainingHeight - nextProductHeight) < heights.productsTableStaticHeightWithContinue) {
                        break;
                    }

                    currentPageProducts.push(productsIndex);
                    remainingHeight -= nextProductHeight;
                    productsIndex++;
                }
            }

            if (!productsNormalFooterAdded && !useProductsContinueFooter) {
                productsNormalFooterAdded = true;
            }

            // Reduce the remaining height by the distance between the products and notes tables or the products table footer
            if (useProductsContinueFooter) {
                remainingHeight -= heights.productsTableFooterStaticHeightWithContinue;
            }
            if (!useProductsContinueFooter && productsNormalFooterAdded) {
                remainingHeight -= heights.productsTableFooterStaticHeight;
            }

            // Add the current page to the pages array
            pages.push({
                products: currentPageProducts,
                notes: currentPageNotes,
                remainingHeight: remainingHeight,
                useProductsContinueFooter: useProductsContinueFooter,
            });
        }

        // if the footer was not added, add it now
        if (!productsNormalFooterAdded) {
            pages.push({
                products: [],
                notes: [],
                remainingHeight: MAX_PAGE_HEIGHT - heights.productsTableFooterStaticHeight,
                useProductsContinueFooter: false,
            });
        }

        // add the product filled to any page, so it can fill up the space.
        for (const page of pages) {
            if (page.products.length === 0) {
                page.products.push(products.length - 1)
            }
        }


        return pages.map((page, index, array) => {
            const productsEntity = {
                name: PrintCustomerStatementViewTableTypes.items,
                range: [
                    page.products.reduce((min, p) => p < min ? p : min, Number.MAX_SAFE_INTEGER),
                    page.products.reduce((max, p) => p > max ? p : max, Number.MIN_SAFE_INTEGER),
                ].filter(e => e !== Number.MIN_SAFE_INTEGER && e !== Number.MAX_SAFE_INTEGER),
                useContinueFooter: page.useProductsContinueFooter,
                spacerHeight: page.remainingHeight,
            }
            const entities = [];
            if (productsEntity.range.length === 2 || !productsEntity.useContinueFooter) {
                entities.push(productsEntity);
            }
            return ({
                entities: entities,
                key: createUUId(),
                pageNumber: index + 1,
                totalPages: array.length,
            })
        });
    }, [])

    /**
     * Sets the pages that are to be rendered for printing.
     * @type {(function(): void)}
     */
    const setPrintPages = useCallback(() => {
        const elements = getElements();
        const heights = getHeights(elements);
        setPages(createPages(elements, heights));
    }, [createPages])

    /**
     * With each change in Invoice:
     * - sets the ready state based on the existence of the invoice.
     * - if the ready state is set to true, waits for 2 seconds and then calculates the necessary info from the DOM.
     */
    useEffect(() => {
        if (!statement || !partner) {
            return setPrintInfoReadyReady(false);
        }
        // give browser to render its elements and load the logo.
        setTimeout(() => {
            setPrintPages();
            setPrintInfoReadyReady(true);
        }, 2000)
    }, [statement, partner, setPrintPages])


    return (
        <>
            <div
                ref={printPreviewRootRef}
                className={'print-customer-outstanding-statement-view'}
                style={printPreviewScaleStyle}
                data-mui-color-scheme="light"
            >
                {
                    !ready &&
                    <div className={'min-vh-100 d-flex justify-content-center align-items-center'}>
                        <div className={'loading print-customer-outstanding-statement-page'}>
                            <div/>
                        </div>
                    </div>
                }
                {
                    ready && !!statement &&
                    <div className={'pages'}>
                        {
                            pages.map(page => (
                                <PrintCustomerOutstandingStatementPage
                                    key={page.key}
                                    page={page}
                                />
                            ))
                        }
                    </div>
                }
                {
                    !!statement && !!partner &&
                    <PrintCustomerOutstandingStatementTemplate
                        statement={statement}
                        partner={partner}
                    />
                }
            </div>
            <WarningDialog
                open={showWarningDialog}
                close={() => setShowWarningDialog(false)}
            />
        </>
    )
}

const WarningDialog = ({open, close}) => {

    return (
        <>
            <Dialog open={open} setOpen={close} fullWidth={false}>
                <DialogTitle>
                    We Apologize for the Inconvenience
                </DialogTitle>
                <DialogContent>
                    <DialogContentText variant={'body1'} color={'text.primary'}>
                        This statement currently shows the outstanding invoices from the latest {MaxInvoiceCountForStatement} invoices.
                        If you think this customer has more outstanding invoices out of this range, please contact our support team.
                        <br/>
                        <br/>
                        We apologize for the inconvenience, we are diligently working to improve this feature in the coming updates.
                    </DialogContentText>
                    <Button onClick={close} fullWidth size={'medium'} color="primary" autoFocus sx={{marginBlockStart: 2}}>
                        Got it
                    </Button>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default PrintCustomerOutstandingStatementView;
